import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ApiContext from './ApiContext';
import VideoThumbnail from './VideoThumbnail';
import LoadingCircle from './LoadingCircle';

// "Aktuell" shows every recording of the last RECENT_DAYS days (a rolling window
// always covers the previous weekend, which is when the dive base is busiest),
// but never fewer than MIN_RECENT_VIDEOS so the page does not look empty after
// a quiet week.
const RECENT_DAYS = 7;
const MIN_RECENT_VIDEOS = 6;

const allTags = ['Taucher', 'Freitaucher', 'Tauchdrohne', 'Anomalien', 'Karpfen', 'Hecht', 'Goldfisch', 'Flussbarsch', 'Sonnenbarsch', 'Rotauge', 'Karausche', 'Flusskrebs', 'Wels', 'Aal', 'Kormoran', '★★★'];

const VideoGallery = ({ viewMode, setViewMode }) => {
    const { baseUrl } = React.useContext(ApiContext);
    const [groupedVideos, setGroupedVideos] = useState({});
    const [currentVideo, setCurrentVideo] = useState(null);
    const [isOverlayVisible, setIsOverlayVisible] = useState(false);
    const [activeTags, setActiveTags] = useState([]);
    const [filterTags, setFilterTags] = useState([]);
    const [initialLoading, setInitialLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [confirmTagChanges, setConfirmTagChanges] = useState(false);
    const [confirmThumbnailChange, setConfirmThumbnailChange] = useState(false);
    const [capturedFrame, setCapturedFrame] = useState(null);
    const viewModeRef = useRef(viewMode);
    const videoRef = useRef(null);
    const closeVideoRef = useRef(null);

    const parseIsoDate = (dateString) => {
        const parsed = new Date(dateString);
        return Number.isNaN(parsed.getTime()) ? null : parsed;
    };

    const formatLongGermanDate = (dateString) => {
        const parsed = parseIsoDate(dateString);
        if (!parsed) return dateString;
        return parsed.toLocaleDateString('de-DE', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
    };

    const bytesToMB = (bytes) => ((bytes || 0) / 1048576).toFixed(1);

    const getTagCategoryClass = (tag) => {
        if (['Taucher', 'Freitaucher', 'Tauchdrohne'].includes(tag)) return 'tag-blue';
        if (tag === '★★★') return 'tag-gold';
        if (['Karpfen', 'Hecht', 'Goldfisch', 'Flussbarsch', 'Sonnenbarsch', 'Rotauge', 'Karausche', 'Flusskrebs', 'Wels', 'Aal', 'Kormoran'].includes(tag)) return 'tag-green';
        return 'tag-gray';
    };

    const allVideosSorted = useMemo(() => Object.values(groupedVideos).flat().sort((a, b) => {
        const da = parseIsoDate(a.creationDate);
        const db = parseIsoDate(b.creationDate);
        if (!da && !db) return 0;
        if (!da) return 1;
        if (!db) return -1;
        if (db.getTime() !== da.getTime()) return db.getTime() - da.getTime();
        return (b.creationTime || '').localeCompare(a.creationTime || '');
    }), [groupedVideos]);

    const featuredVideo = useMemo(() => {
        const newestStarredVideo = allVideosSorted.find((video) => (video.tags || []).includes('★★★'));
        return newestStarredVideo || allVideosSorted[0] || null;
    }, [allVideosSorted]);

    const filteredVideos = useMemo(() => allVideosSorted.filter((video) => {
        const passesTags = filterTags.length === 0 || (video.tags && filterTags.every((tag) => video.tags.includes(tag)));
        const searchHay = `${video.filename} ${(video.tags || []).join(' ')}`.toLowerCase();
        const passesSearch = !searchTerm || searchHay.includes(searchTerm.toLowerCase());
        return passesTags && passesSearch;
    }), [allVideosSorted, filterTags, searchTerm]);
    const recentVideos = useMemo(() => {
        const cutoff = new Date();
        cutoff.setDate(cutoff.getDate() - RECENT_DAYS);
        // Compare as local YYYY-MM-DD strings: creationDate is a plain date, so
        // this avoids any UTC-vs-local midnight edge case.
        const cutoffDate = `${cutoff.getFullYear()}-${String(cutoff.getMonth() + 1).padStart(2, '0')}-${String(cutoff.getDate()).padStart(2, '0')}`;
        const recent = filteredVideos.filter((video) => video.creationDate >= cutoffDate);
        return recent.length >= MIN_RECENT_VIDEOS
            ? { videos: recent, isWindow: true }
            : { videos: filteredVideos.slice(0, MIN_RECENT_VIDEOS), isWindow: false };
    }, [filteredVideos]);

    const archiveTimeline = useMemo(() => {
        const map = {};
        filteredVideos.forEach((v) => {
            if (!map[v.creationDate]) map[v.creationDate] = [];
            map[v.creationDate].push(v);
        });
        return Object.keys(map).sort((a, b) => Date.parse(b) - Date.parse(a)).map((date) => ({ date, videos: map[date] }));
    }, [filteredVideos]);

    const quickNav = useMemo(() => {
        const monthMap = new Map();
        const yearMap = new Map();
        archiveTimeline.forEach((group) => {
            const parsed = parseIsoDate(group.date);
            if (!parsed) return;
            const year = parsed.getFullYear();
            const month = parsed.getMonth();
            const monthKey = `${year}-${month}`;
            if (!monthMap.has(monthKey)) {
                monthMap.set(monthKey, {
                    type: 'month',
                    key: monthKey,
                    year,
                    month,
                    label: parsed.toLocaleDateString('de-DE', { month: 'short' }).replace('.', ''),
                    anchorId: `date-${group.date}`
                });
            }
            if (!yearMap.has(year)) {
                yearMap.set(year, {
                    type: 'year',
                    key: `year-${year}`,
                    year,
                    anchorId: `date-${group.date}`,
                    preview: parsed.toLocaleDateString('de-DE', { month: 'short', year: '2-digit' })
                });
            }
        });

        const yearItems = [...yearMap.values()];
        const monthItems = [...monthMap.values()];
        const combined = [...yearItems, ...monthItems].sort((a, b) => {
            if (a.year !== b.year) return b.year - a.year;
            const am = a.type === 'year' ? 12 : a.month;
            const bm = b.type === 'year' ? 12 : b.month;
            if (bm !== am) return bm - am;
            return a.type === 'year' ? -1 : 1;
        });
        return combined;
    }, [archiveTimeline]);

    const playVideo = useCallback((video) => {
        setCurrentVideo(video);
        setActiveTags(video.tags || []);
        setConfirmTagChanges(false);
        setConfirmThumbnailChange(false);
        setCapturedFrame(null);
        setIsOverlayVisible(true);
        window.history.pushState({}, '', `${window.location.origin}/?findVideo=${video.filename}${viewModeRef.current === 'archiv' ? '&view=archiv' : ''}`);
    }, []);

    const closeVideo = () => {
        if (currentVideo && confirmTagChanges) updateTagsInDatabase(currentVideo.filename, activeTags);
        if (currentVideo && confirmThumbnailChange && capturedFrame) updateThumbnailInDatabase(currentVideo.filename, capturedFrame.base64);
        setIsOverlayVisible(false);
        setConfirmTagChanges(false);
        setConfirmThumbnailChange(false);
        setCapturedFrame(null);
        window.history.pushState({}, '', `${window.location.origin}${viewMode === 'archiv' ? '/?view=archiv' : ''}`);
    };

    // Grabs the currently shown frame from the overlay player (paused or not),
    // downscales it to at most 1280px width and compresses it until the JPEG
    // stays comfortably below the API's 500KB limit. Requires the video element
    // to be loaded with crossOrigin="anonymous", otherwise the canvas is
    // tainted and toBlob() throws a SecurityError.
    const captureThumbnailFrame = async () => {
        const videoEl = videoRef.current;
        if (!videoEl || videoEl.readyState < 2) return;
        try {
            const maxWidth = 1280;
            const scale = Math.min(1, maxWidth / videoEl.videoWidth);
            const canvas = document.createElement('canvas');
            canvas.width = Math.round(videoEl.videoWidth * scale);
            canvas.height = Math.round(videoEl.videoHeight * scale);
            canvas.getContext('2d').drawImage(videoEl, 0, 0, canvas.width, canvas.height);

            const toJpegBlob = (quality) => new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', quality));
            let quality = 0.85;
            let blob = await toJpegBlob(quality);
            while (blob && blob.size > 300 * 1024 && quality > 0.4) {
                quality -= 0.1;
                blob = await toJpegBlob(quality);
            }
            if (!blob) throw new Error('Canvas toBlob returned null');

            const bytes = new Uint8Array(await blob.arrayBuffer());
            let binary = '';
            const chunkSize = 0x8000;
            for (let i = 0; i < bytes.length; i += chunkSize) {
                binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
            }
            setCapturedFrame({ base64: btoa(binary), dataUrl: URL.createObjectURL(blob) });
        } catch (error) {
            console.error('Error capturing thumbnail frame:', error);
        }
    };

    const toggleTag = (tag) => setActiveTags((prev) => prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]);
    const toggleFilterTag = (tag) => setFilterTags((prev) => prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]);

    const fetchVideos = useCallback((retries = 3, delay = 2000) => {
        fetch(`${baseUrl}/videos-with-tags`, { cache: 'no-store' })
            .then((response) => response.ok ? response.json() : Promise.reject(new Error('API request failed')))
            .then((data) => {
                setGroupedVideos(data.videos);
                const params = new URLSearchParams(window.location.search);
                const videoFilename = params.get('findVideo');
                if (videoFilename) {
                    const found = Object.values(data.videos).flat().find((v) => v.filename === videoFilename);
                    if (found) playVideo(found);
                }
            })
            .catch((error) => {
                console.error('Error fetching videos:', error);
                if (retries > 0) setTimeout(() => fetchVideos(retries - 1, delay), delay);
            })
            .finally(() => { setInitialLoading(false); });
    }, [baseUrl, playVideo]);

    const updateTagsInDatabase = (videoFilename, tags) => {
        fetch(`${baseUrl}/tags`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ filename: videoFilename, tags })
        })
            .then((response) => { if (!response.ok) throw new Error('Network response was not ok'); return response.json(); })
            .then(() => {
                setGroupedVideos((prev) => {
                    const copy = { ...prev };
                    for (const date in copy) {
                        const idx = copy[date].findIndex((v) => v.filename === videoFilename);
                        if (idx !== -1) { copy[date][idx] = { ...copy[date][idx], tags }; break; }
                    }
                    return copy;
                });
            })
            .catch((error) => console.error('Error updating tags:', error));
    };

    const updateThumbnailInDatabase = (videoFilename, imageBase64) => {
        fetch(`${baseUrl}/thumbnail`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ filename: videoFilename, imageBase64 })
        })
            .then((response) => { if (!response.ok) throw new Error('Network response was not ok'); return response.json(); })
            .then((data) => {
                // The API appends ?v=<thumbnailUpdatedAt> as cache-buster; mirror
                // that locally so the gallery shows the new image right away.
                setGroupedVideos((prev) => {
                    const copy = { ...prev };
                    for (const date in copy) {
                        const idx = copy[date].findIndex((v) => v.filename === videoFilename);
                        if (idx !== -1) {
                            const baseThumbUrl = copy[date][idx].thumbnailUrl.split('?')[0];
                            copy[date][idx] = { ...copy[date][idx], thumbnailUrl: `${baseThumbUrl}?v=${data.thumbnailUpdatedAt}` };
                            break;
                        }
                    }
                    return copy;
                });
            })
            .catch((error) => console.error('Error updating thumbnail:', error));
    };

    useEffect(() => { fetchVideos(); }, [fetchVideos]);
    useEffect(() => { setActiveTags(currentVideo?.tags || []); }, [currentVideo]);
    useEffect(() => { viewModeRef.current = viewMode; }, [viewMode]);
    // ESC must run the full close path (tag/thumbnail persistence), not just
    // hide the overlay; the ref always points at the latest closeVideo closure.
    useEffect(() => { closeVideoRef.current = closeVideo; });
    useEffect(() => {
        if (!isOverlayVisible) return;
        const onKeyDown = (e) => { if (e.key === 'Escape') closeVideoRef.current(); };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [isOverlayVisible]);

    return (
        <>
        <div className={`video-gallery ${initialLoading ? 'gallery-hidden' : 'gallery-visible'}`}>
            {viewMode === 'aktuell' && (
                <>
                    <section className="hero-layout">
                        <div className="hero-copy">
                            <p className="eyebrow">Unterwasserkamera am Riesenstein</p>
                            <h1>Livevideo & Aufzeichnungen vom Riesenstein</h1>
                            <p>Unser fest installiertes Kamerasystem zeigt die faszinierende Unterwasserwelt des Riesenstein – rund um die Uhr live und im Archiv.</p>
                            <div className="hero-actions"><a className="primary-btn" href="https://live.tauchbasis-riesenstein.com/" target="_blank" rel="noreferrer">Live-Kamera ansehen</a></div>
                        </div>
                        {featuredVideo && <div className="featured-panel" onClick={() => playVideo(featuredVideo)} role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') playVideo(featuredVideo); }}><span className="featured-badge">Featured</span><img src={featuredVideo.thumbnailUrl} alt={featuredVideo.filename} /><div className="featured-meta"><p>{featuredVideo.creationDate} • {featuredVideo.creationTime} • {featuredVideo.duration}</p></div></div>}
                    </section>
                    <div className="tag-filter-buttons"><span className="filter-title">Tags & Filter</span>{allTags.map((tag) => <button key={tag} onClick={() => toggleFilterTag(tag)} className={`${getTagCategoryClass(tag)} ${filterTags.includes(tag) ? 'active' : ''}`}>{tag}</button>)}</div>
                    <p className="eyebrow recent-title">{recentVideos.isWindow ? `Aufnahmen der letzten ${RECENT_DAYS} Tage` : 'Neueste Aufnahmen'}</p>
                    <section className="popular-grid">{recentVideos.videos.map((video) => <VideoThumbnail key={video.filename} video={video} onPlay={playVideo} compact />)}</section>
                </>
            )}

            {viewMode === 'archiv' && (
                <section className="archive-view">
                    <div className="archive-main">
                        <h1 className="archive-title">Archiv</h1>
                        <p className="archive-subtitle">Aufzeichnungen der Unterwasserkamera im Riesenstein</p>
                        <div className="archive-toolbar">
                            <input placeholder="Videos durchsuchen..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                            <div className="tag-filter-buttons">{allTags.map((tag) => <button key={tag} onClick={() => toggleFilterTag(tag)} className={`${getTagCategoryClass(tag)} ${filterTags.includes(tag) ? 'active' : ''}`}>{tag}</button>)}</div>
                        </div>
                        <div className="timeline">
                            {archiveTimeline.map((group) => (
                                <div key={group.date} id={`date-${group.date}`} className="timeline-group">
                                    <div className="timeline-date">{formatLongGermanDate(group.date)} <span>• {group.videos.length} Videos</span></div>
                                    <div className="timeline-cards">
                                        {group.videos.map((video) => (
                                            <article key={video.filename} className="archive-card" onClick={() => playVideo(video)}>
                                                <img src={video.thumbnailUrl} alt={video.filename} loading="lazy" decoding="async" />
                                                <div className="archive-card-body">
                                                    <div className="video-tags">{(video.tags || []).map((tag, i) => <span key={i} className={`video-tag ${getTagCategoryClass(tag)}`}>{tag}</span>)}</div>
                                                    <p>Datum: {video.creationDate} &nbsp; Uhrzeit: {video.creationTime}</p>
                                                    <p>Dateigröße: {bytesToMB(video.size)} MB &nbsp; Dauer: {video.duration}</p>
                                                </div>
                                            </article>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                    <aside className="quick-scroll">
                        <div className="quick-track">
                            {quickNav.map((entry) => (
                                <a key={entry.key} href={`#${entry.anchorId}`} className={entry.type === 'year' ? 'quick-year-pill' : 'quick-month-dot'} title={entry.type === 'year' ? String(entry.year) : `${entry.month + 1}.${entry.year}`}>
                                    {entry.type === 'year' ? <><span>{entry.year}</span><small>{entry.preview}</small></> : <span className="quick-month-label">{entry.label}</span>}
                                </a>
                            ))}
                        </div>
                    </aside>
                </section>
            )}

            {isOverlayVisible && currentVideo && <div className="video-overlay visible" onClick={closeVideo}><div className="video-wrapper" onClick={(e) => e.stopPropagation()}><div className="video-middle-column"><div className="video-container"><video ref={videoRef} controls crossOrigin="anonymous" src={currentVideo.videoUrl} autoPlay></video><div className="video-container-tags">{allTags.map((tag) => <span key={tag} className={`video-tag ${getTagCategoryClass(tag)} ${activeTags.includes(tag) ? 'active' : 'inactive'}`} onClick={() => toggleTag(tag)}>{tag}</span>)}</div><div className="overlay-controls-row"><label className="tag-confirm-row"><input type="checkbox" checked={confirmTagChanges} onChange={(e) => setConfirmTagChanges(e.target.checked)} /> Tag-Änderungen beim Schließen übernehmen</label><button className="thumb-capture-btn" onClick={captureThumbnailFrame}>Aktuellen Frame als Thumbnail vormerken</button>{capturedFrame && <img className="thumb-capture-preview" src={capturedFrame.dataUrl} alt="Neues Thumbnail" />}<label className={`tag-confirm-row ${capturedFrame ? '' : 'thumb-confirm-disabled'}`}><input type="checkbox" disabled={!capturedFrame} checked={confirmThumbnailChange} onChange={(e) => setConfirmThumbnailChange(e.target.checked)} /> Thumbnail beim Schließen aktualisieren</label></div></div></div></div><button className="close-button" onClick={(e) => { e.stopPropagation(); closeVideo(); }}>x</button></div>}
        </div>
        {initialLoading && <div className="initial-overlay"><LoadingCircle /></div>}
        </>
    );
};

export default VideoGallery;
